import { useCallback, useSyncExternalStore } from "react";

const PINNED_BRANCHES_KEY = "mst:pinnedBranches";
const PINNED_CLIENTS_KEY = "mst:pinnedClients";

const EMPTY: string[] = [];

function createPinnedStore(storageKey: string) {
  const listeners = new Set<() => void>();
  let cachedRaw: string | null = null;
  let cachedValue: string[] = EMPTY;

  function getSnapshot(): string[] {
    const raw = localStorage.getItem(storageKey);
    if (raw === cachedRaw) return cachedValue;
    cachedRaw = raw;
    try {
      cachedValue = raw ? JSON.parse(raw) : EMPTY;
    } catch {
      cachedValue = EMPTY;
    }
    return cachedValue;
  }

  function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  function update(ids: string[]) {
    localStorage.setItem(storageKey, JSON.stringify(ids));
    cachedRaw = null;
    listeners.forEach((l) => l());
  }

  return { getSnapshot, subscribe, update };
}

const pinnedBranchesStore = createPinnedStore(PINNED_BRANCHES_KEY);
const pinnedClientsStore = createPinnedStore(PINNED_CLIENTS_KEY);

function usePinnedStore(store: ReturnType<typeof createPinnedStore>) {
  const pinnedIds = useSyncExternalStore(store.subscribe, store.getSnapshot);

  const toggle = useCallback(
    (id: string) => {
      const current = store.getSnapshot();
      const next = current.includes(id)
        ? current.filter((v) => v !== id)
        : [...current, id];
      store.update(next);
    },
    [store],
  );

  const isPinned = useCallback(
    (id: string) => pinnedIds.includes(id),
    [pinnedIds],
  );

  return { pinnedIds, toggle, isPinned };
}

export function usePinnedBranches() {
  return usePinnedStore(pinnedBranchesStore);
}

export function usePinnedClients() {
  return usePinnedStore(pinnedClientsStore);
}
